/**
 * V2MOM Store
 *
 * One V2MOM document per leader / org unit / period: Vision, Values, Methods,
 * Obstacles, Measures. Documents live in `v2moms`; the ordered lines live in
 * `v2mom_entries`. A document can align to a parent V2MOM (the boss's), which
 * is how the CEO → SVP → VP → director cascade is expressed.
 *
 * The document and its process are written only through the governed RPCs of
 * migration 945 (create / update / delete / submit / approve / return /
 * withdraw / lock / request reopen / reopen / decline / ask / withdraw ask) —
 * one write path per act (build contract §2.3c). The database decides who may
 * act; the store carries its answer back. The lines (`v2mom_entries`) are the
 * one remaining direct write, and the database lets them through only while
 * the document is a draft and only for the people who may edit it.
 *
 * No governance transition flows through here — a V2MOM is a communication
 * artifact; its statuses are document states, not authority.
 */

import { create } from 'zustand';
import type { Json } from '../../types/database.generated';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { getCurrentWorkspaceId, generateUUID } from '../../lib/supabaseSync';
import type { Database } from '../../types/database.generated';
import { useUserStore } from './userStore';
import { useResponsibilityStore } from './responsibilityStore';
import {
  nextV2momPosition,
  v2momErrorMessage,
  type V2mom,
  type V2momAsk,
  type V2momAskStatus,
  type V2momEntry,
  type V2momEntryKind,
  type V2momEvent,
  type V2momStatus,
} from '../../lib/v2mom/v2momModel';

export type { V2mom, V2momAsk, V2momEntry, V2momEntryKind, V2momEvent, V2momStatus } from '../../lib/v2mom/v2momModel';

type V2momRow = Database['public']['Tables']['v2moms']['Row'];
type V2momEntryRow = Database['public']['Tables']['v2mom_entries']['Row'];
type V2momAskRow = Database['public']['Tables']['v2mom_asks']['Row'];
type V2momEventRow = Database['public']['Tables']['v2mom_events']['Row'];

// ── Inputs ────────────────────────────────────────────────────

export interface CreateV2momInput {
  title?: string;
  parentId?: string | null;
  orgUnitId?: string | null;
  periodLabel?: string | null;
  dueOn?: string | null;
}

export interface UpdateV2momInput {
  title?: string;
  vision?: string | null;
  ownerId?: string | null;
  orgUnitId?: string | null;
  parentId?: string | null;
  periodLabel?: string | null;
  dueOn?: string | null;
}

export interface CreateEntryInput {
  kind: V2momEntryKind;
  title: string;
  detail?: string | null;
  target?: string | null;
  methodId?: string | null;
  objectiveId?: string | null;
}

export interface UpdateEntryInput {
  title?: string;
  detail?: string | null;
  target?: string | null;
  objectiveId?: string | null;
  position?: number;
}

export interface AskForV2momsInput {
  parentId: string;
  userIds: string[];
  dueOn?: string | null;
  note?: string | null;
}

export type Result = { success: boolean; error?: string };
/** A workflow act's answer: the refreshed document, and for submit / request-reopen which way it went. */
export type ActResult = Result & { outcome?: 'in_review' | 'published' | 'requested' | 'reopened'; v2mom?: V2mom };

// ── Row mappers ───────────────────────────────────────────────

function rowToV2mom(row: V2momRow): V2mom {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    title: row.title ?? 'Untitled V2MOM',
    vision: row.vision ?? null,
    ownerId: row.owner_id ?? null,
    orgUnitId: row.org_unit_id ?? null,
    parentId: row.parent_id ?? null,
    periodLabel: row.period_label ?? null,
    status: (row.status as V2momStatus) ?? 'draft',
    publishedAt: row.published_at ?? null,
    lockedAt: row.locked_at ?? null,
    createdBy: row.created_by ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    dueOn: row.due_on ?? null,
    submittedAt: row.submitted_at ?? null,
    submittedBy: row.submitted_by ?? null,
    reviewedAt: row.reviewed_at ?? null,
    reviewedBy: row.reviewed_by ?? null,
    reviewNote: row.review_note ?? null,
    publishedBy: row.published_by ?? null,
    lockedBy: row.locked_by ?? null,
    reopenRequestedAt: row.reopen_requested_at ?? null,
    reopenRequestedBy: row.reopen_requested_by ?? null,
    reopenNote: row.reopen_note ?? null,
  };
}

function rowToEntry(row: V2momEntryRow): V2momEntry {
  return {
    id: row.id,
    v2momId: row.v2mom_id,
    workspaceId: row.workspace_id,
    kind: row.kind as V2momEntryKind,
    methodId: row.method_id ?? null,
    position: row.position ?? 0,
    title: row.title,
    detail: row.detail ?? null,
    target: row.target ?? null,
    objectiveId: row.objective_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToAsk(row: V2momAskRow): V2momAsk {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    parentV2momId: row.parent_v2mom_id,
    askedUserId: row.asked_user_id,
    askedBy: row.asked_by ?? null,
    dueOn: row.due_on ?? null,
    note: row.note ?? null,
    status: (row.status as V2momAskStatus) ?? 'open',
    v2momId: row.v2mom_id ?? null,
    fulfilledAt: row.fulfilled_at ?? null,
    withdrawnAt: row.withdrawn_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToEvent(row: V2momEventRow): V2momEvent {
  return {
    id: row.id,
    v2momId: row.v2mom_id,
    workspaceId: row.workspace_id,
    event: row.event,
    actorId: row.actor_id ?? null,
    fromStatus: row.from_status ?? null,
    toStatus: row.to_status ?? null,
    note: row.note ?? null,
    createdAt: row.created_at,
  };
}

/** The shape every 945 RPC answers with. */
interface RpcAnswer {
  success?: boolean;
  error?: string;
  outcome?: ActResult['outcome'];
  v2mom?: V2momRow & { reviewer_id?: string | null };
  ask?: V2momAskRow;
  asks?: V2momAskRow[];
  skipped?: { user_id: string; reason: string }[];
}

function readAnswer(data: Json | null, error: { message: string } | null): RpcAnswer {
  if (error) return { success: false, error: error.message };
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { success: false, error: 'No answer from the database.' };
  return data as unknown as RpcAnswer;
}

/** The person's "what needs you" list changes with every act here. */
function refreshResponsibilities(): void {
  const userId = useUserStore.getState().userId;
  if (!userId) return;
  void useResponsibilityStore.getState().refreshResponsibilities(userId);
}

// ── Store ─────────────────────────────────────────────────────

interface V2momState {
  v2moms: V2mom[];
  /** Entries for every document in the workspace, keyed by document id. */
  entriesByDoc: Record<string, V2momEntry[]>;
  /** Every ask in the workspace (open, fulfilled and withdrawn). */
  asks: V2momAsk[];
  /** The record of each document, fetched when its editor opens. */
  eventsByDoc: Record<string, V2momEvent[]>;
  isLoading: boolean;
  syncError: string | null;

  fetchV2moms: () => Promise<void>;
  fetchEvents: (v2momId: string) => Promise<void>;

  createV2mom: (input?: CreateV2momInput) => Promise<{ v2mom: V2mom | null; error?: string }>;
  /** Start the draft an ask is for: aligned to the ask's parent, owned by the person asked, due when the ask says. */
  startFromAsk: (askId: string, title?: string) => Promise<{ v2mom: V2mom | null; error?: string }>;
  updateV2mom: (id: string, updates: UpdateV2momInput) => Promise<Result>;
  deleteV2mom: (id: string) => Promise<Result>;

  submit: (id: string, note?: string | null) => Promise<ActResult>;
  withdrawSubmission: (id: string) => Promise<ActResult>;
  approve: (id: string, note?: string | null) => Promise<ActResult>;
  returnToAuthor: (id: string, note: string) => Promise<ActResult>;
  lock: (id: string) => Promise<ActResult>;
  requestReopen: (id: string, note: string) => Promise<ActResult>;
  reopen: (id: string, note?: string | null) => Promise<ActResult>;
  declineReopen: (id: string, note?: string | null) => Promise<ActResult>;
  askFor: (input: AskForV2momsInput) => Promise<Result & { asked: number; skipped: { userId: string; reason: string }[] }>;
  withdrawAsk: (askId: string) => Promise<Result>;

  createEntry: (v2momId: string, input: CreateEntryInput) => Promise<{ entry: V2momEntry | null; error?: string }>;
  updateEntry: (entryId: string, updates: UpdateEntryInput) => Promise<Result>;
  moveEntry: (entryId: string, direction: 'up' | 'down') => Promise<Result>;
  deleteEntry: (entryId: string) => Promise<Result>;
}

function findEntry(state: V2momState, entryId: string): V2momEntry | undefined {
  for (const list of Object.values(state.entriesByDoc)) {
    const hit = list.find((e) => e.id === entryId);
    if (hit) return hit;
  }
  return undefined;
}

export const useV2momStore = create<V2momState>((set, get) => {
  /** Put the row the database answered with in place of ours, then refresh what else it may have changed. */
  function absorb(answer: RpcAnswer, docId: string): ActResult {
    if (!answer.success) {
      return { success: false, error: v2momErrorMessage(answer.error, 'The change did not save.') };
    }
    const v2mom = answer.v2mom ? rowToV2mom(answer.v2mom) : undefined;
    if (v2mom) {
      set((state) => ({
        v2moms: state.v2moms.some((d) => d.id === v2mom.id)
          ? state.v2moms.map((d) => (d.id === v2mom.id ? v2mom : d))
          : [v2mom, ...state.v2moms],
      }));
    }
    void get().fetchEvents(docId);
    void refreshAsks();
    refreshResponsibilities();
    return { success: true, outcome: answer.outcome, v2mom };
  }

  async function refreshAsks(): Promise<void> {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    const supabase = getTypedSupabaseClient();
    const { data, error } = await supabase.from('v2mom_asks').select('*').eq('workspace_id', workspaceId).order('created_at', { ascending: false });
    if (error) {
      console.error('[V2momStore] fetch asks error:', error);
      return;
    }
    set({ asks: (data ?? []).map(rowToAsk) });
  }

  async function act(
    docId: string,
    call: () => PromiseLike<{ data: Json | null; error: { message: string } | null }>,
  ): Promise<ActResult> {
    const { data, error } = await call();
    return absorb(readAnswer(data, error), docId);
  }

  return {
    v2moms: [],
    entriesByDoc: {},
    asks: [],
    eventsByDoc: {},
    isLoading: false,
    syncError: null,

    fetchV2moms: async () => {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) return;
      const supabase = getTypedSupabaseClient();

      set({ isLoading: true, syncError: null });
      try {
        const [docsRes, entriesRes, asksRes] = await Promise.all([
          supabase
            .from('v2moms')
            .select('*')
            .eq('workspace_id', workspaceId)
            .order('created_at', { ascending: false }),
          supabase
            .from('v2mom_entries')
            .select('*')
            .eq('workspace_id', workspaceId)
            .order('position', { ascending: true }),
          supabase
            .from('v2mom_asks')
            .select('*')
            .eq('workspace_id', workspaceId)
            .order('created_at', { ascending: false }),
        ]);

        const failed = docsRes.error ?? entriesRes.error ?? asksRes.error;
        if (failed) {
          console.error('[V2momStore] fetch error:', failed);
          set({ isLoading: false, syncError: failed.message });
          return;
        }

        const entriesByDoc: Record<string, V2momEntry[]> = {};
        for (const row of entriesRes.data ?? []) {
          const entry = rowToEntry(row);
          (entriesByDoc[entry.v2momId] ??= []).push(entry);
        }

        set({
          v2moms: (docsRes.data ?? []).map(rowToV2mom),
          entriesByDoc,
          asks: (asksRes.data ?? []).map(rowToAsk),
          isLoading: false,
        });
      } catch (err) {
        console.error('[V2momStore] fetch exception:', err);
        set({ isLoading: false, syncError: 'Failed to load V2MOMs' });
      }
    },

    fetchEvents: async (v2momId) => {
      const supabase = getTypedSupabaseClient();
      const { data, error } = await supabase
        .from('v2mom_events')
        .select('*')
        .eq('v2mom_id', v2momId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) {
        console.error('[V2momStore] fetch events error:', error);
        return;
      }
      set((state) => ({ eventsByDoc: { ...state.eventsByDoc, [v2momId]: (data ?? []).map(rowToEvent) } }));
    },

    createV2mom: async (input = {}) => {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) return { v2mom: null, error: 'No workspace selected' };
      const supabase = getTypedSupabaseClient();

      const { data, error } = await supabase.rpc('create_v2mom', {
        p_workspace_id: workspaceId,
        p_title: input.title?.trim() || 'Untitled V2MOM',
        p_parent_id: input.parentId ?? undefined,
        p_org_unit_id: input.orgUnitId ?? undefined,
        p_period_label: input.periodLabel ?? undefined,
        p_due_on: input.dueOn ?? undefined,
      });
      const answer = readAnswer(data, error);
      if (!answer.success || !answer.v2mom) {
        console.error('[V2momStore] createV2mom refused:', answer.error);
        return { v2mom: null, error: v2momErrorMessage(answer.error, 'Could not create the V2MOM.') };
      }
      const v2mom = rowToV2mom(answer.v2mom);
      set((state) => ({
        v2moms: [v2mom, ...state.v2moms],
        entriesByDoc: { ...state.entriesByDoc, [v2mom.id]: [] },
      }));
      refreshResponsibilities();
      return { v2mom };
    },

    startFromAsk: async (askId, title) => {
      const supabase = getTypedSupabaseClient();
      const { data, error } = await supabase.rpc('create_v2mom', {
        p_ask_id: askId,
        p_title: title?.trim() || undefined,
      });
      const answer = readAnswer(data, error);
      if (!answer.success || !answer.v2mom) {
        console.error('[V2momStore] startFromAsk refused:', answer.error);
        return { v2mom: null, error: v2momErrorMessage(answer.error, 'Could not start the V2MOM.') };
      }
      const v2mom = rowToV2mom(answer.v2mom);
      set((state) => ({
        v2moms: [v2mom, ...state.v2moms],
        entriesByDoc: { ...state.entriesByDoc, [v2mom.id]: [] },
        asks: state.asks.map((a) => (a.id === askId ? { ...a, v2momId: v2mom.id } : a)),
      }));
      refreshResponsibilities();
      return { v2mom };
    },

    updateV2mom: async (id, updates) => {
      const supabase = getTypedSupabaseClient();
      const patch: Record<string, Json> = {};
      if (updates.title !== undefined) patch.title = updates.title;
      if (updates.vision !== undefined) patch.vision = updates.vision;
      if (updates.ownerId !== undefined) patch.owner_id = updates.ownerId;
      if (updates.orgUnitId !== undefined) patch.org_unit_id = updates.orgUnitId;
      if (updates.parentId !== undefined) patch.parent_id = updates.parentId;
      if (updates.periodLabel !== undefined) patch.period_label = updates.periodLabel;
      if (updates.dueOn !== undefined) patch.due_on = updates.dueOn;
      if (Object.keys(patch).length === 0) return { success: true };

      // Optimistic, rolled back to the database's answer either way.
      set((state) => ({
        v2moms: state.v2moms.map((d) => (d.id === id ? { ...d, ...updates } : d)),
      }));

      const { data, error } = await supabase.rpc('update_v2mom', { p_v2mom_id: id, p_patch: patch });
      const answer = readAnswer(data, error);
      if (!answer.success) {
        console.error('[V2momStore] updateV2mom refused:', answer.error);
        void get().fetchV2moms();
        return { success: false, error: v2momErrorMessage(answer.error, 'The change did not save.') };
      }
      if (answer.v2mom) {
        const fresh = rowToV2mom(answer.v2mom);
        set((state) => ({ v2moms: state.v2moms.map((d) => (d.id === id ? fresh : d)) }));
      }
      return { success: true };
    },

    deleteV2mom: async (id) => {
      const supabase = getTypedSupabaseClient();
      const { data, error } = await supabase.rpc('delete_v2mom', { p_v2mom_id: id });
      const answer = readAnswer(data, error);
      if (!answer.success) {
        console.error('[V2momStore] deleteV2mom refused:', answer.error);
        return { success: false, error: v2momErrorMessage(answer.error, 'Could not delete the V2MOM.') };
      }
      set((state) => {
        const entriesByDoc = { ...state.entriesByDoc };
        delete entriesByDoc[id];
        const eventsByDoc = { ...state.eventsByDoc };
        delete eventsByDoc[id];
        return {
          v2moms: state.v2moms
            .filter((d) => d.id !== id)
            // Children of a deleted document lose their parent (ON DELETE SET NULL).
            .map((d) => (d.parentId === id ? { ...d, parentId: null } : d)),
          entriesByDoc,
          eventsByDoc,
          // Asks sent from it go with it; asks it was started from lose the link.
          asks: state.asks
            .filter((a) => a.parentV2momId !== id)
            .map((a) => (a.v2momId === id ? { ...a, v2momId: null } : a)),
        };
      });
      refreshResponsibilities();
      return { success: true };
    },

    submit: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('submit_v2mom', { p_v2mom_id: id, p_note: note ?? undefined })),
    withdrawSubmission: (id) =>
      act(id, () => getTypedSupabaseClient().rpc('withdraw_v2mom_submission', { p_v2mom_id: id })),
    approve: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('approve_v2mom', { p_v2mom_id: id, p_note: note ?? undefined })),
    returnToAuthor: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('return_v2mom', { p_v2mom_id: id, p_note: note })),
    lock: (id) => act(id, () => getTypedSupabaseClient().rpc('lock_v2mom', { p_v2mom_id: id })),
    requestReopen: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('request_v2mom_reopen', { p_v2mom_id: id, p_note: note })),
    reopen: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('reopen_v2mom', { p_v2mom_id: id, p_note: note ?? undefined })),
    declineReopen: (id, note) =>
      act(id, () => getTypedSupabaseClient().rpc('decline_v2mom_reopen', { p_v2mom_id: id, p_note: note ?? undefined })),

    askFor: async ({ parentId, userIds, dueOn, note }) => {
      const supabase = getTypedSupabaseClient();
      const { data, error } = await supabase.rpc('ask_for_v2moms', {
        p_parent_v2mom_id: parentId,
        p_user_ids: userIds,
        p_due_on: dueOn ?? undefined,
        p_note: note ?? undefined,
      });
      const answer = readAnswer(data, error);
      if (!answer.success) {
        console.error('[V2momStore] askFor refused:', answer.error);
        return { success: false, error: v2momErrorMessage(answer.error, 'Could not send the asks.'), asked: 0, skipped: [] };
      }
      await refreshAsks();
      void get().fetchEvents(parentId);
      return {
        success: true,
        asked: answer.asks?.length ?? 0,
        skipped: (answer.skipped ?? []).map((s) => ({ userId: s.user_id, reason: s.reason })),
      };
    },

    withdrawAsk: async (askId) => {
      const supabase = getTypedSupabaseClient();
      const { data, error } = await supabase.rpc('withdraw_v2mom_ask', { p_ask_id: askId });
      const answer = readAnswer(data, error);
      if (!answer.success) {
        console.error('[V2momStore] withdrawAsk refused:', answer.error);
        return { success: false, error: v2momErrorMessage(answer.error, 'Could not withdraw the ask.') };
      }
      const ask = answer.ask ? rowToAsk(answer.ask) : null;
      set((state) => ({ asks: state.asks.map((a) => (a.id === askId && ask ? ask : a)) }));
      if (ask) void get().fetchEvents(ask.parentV2momId);
      return { success: true };
    },

    createEntry: async (v2momId, input) => {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) return { entry: null, error: 'No workspace selected' };
      const supabase = getTypedSupabaseClient();

      const existing = get().entriesByDoc[v2momId] ?? [];
      const position = nextV2momPosition(existing, input.kind, input.methodId ?? null);

      const { data, error } = await supabase
        .from('v2mom_entries')
        .insert({
          id: generateUUID(),
          v2mom_id: v2momId,
          workspace_id: workspaceId,
          kind: input.kind,
          method_id: input.methodId ?? null,
          position,
          title: input.title.trim(),
          detail: input.detail ?? null,
          target: input.target ?? null,
          objective_id: input.objectiveId ?? null,
        })
        .select('*')
        .single();

      if (error) {
        console.error('[V2momStore] createEntry error:', error);
        return { entry: null, error: error.message };
      }

      const entry = rowToEntry(data);
      set((state) => ({
        entriesByDoc: {
          ...state.entriesByDoc,
          [v2momId]: [...(state.entriesByDoc[v2momId] ?? []), entry],
        },
      }));
      return { entry };
    },

    updateEntry: async (entryId, updates) => {
      const supabase = getTypedSupabaseClient();
      const current = findEntry(get(), entryId);
      if (!current) return { success: false, error: 'Entry not found' };

      const dbUpdates: Database['public']['Tables']['v2mom_entries']['Update'] = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.detail !== undefined) dbUpdates.detail = updates.detail;
      if (updates.target !== undefined) dbUpdates.target = updates.target;
      if (updates.objectiveId !== undefined) dbUpdates.objective_id = updates.objectiveId;
      if (updates.position !== undefined) dbUpdates.position = updates.position;

      set((state) => ({
        entriesByDoc: {
          ...state.entriesByDoc,
          [current.v2momId]: (state.entriesByDoc[current.v2momId] ?? []).map((e) =>
            e.id === entryId ? { ...e, ...updates } : e,
          ),
        },
      }));

      const { error } = await supabase.from('v2mom_entries').update(dbUpdates).eq('id', entryId);
      if (error) {
        console.error('[V2momStore] updateEntry error:', error);
        void get().fetchV2moms();
        return { success: false, error: error.message };
      }
      return { success: true };
    },

    moveEntry: async (entryId, direction) => {
      const current = findEntry(get(), entryId);
      if (!current) return { success: false, error: 'Entry not found' };
      const siblings = (get().entriesByDoc[current.v2momId] ?? [])
        .filter((e) => e.kind === current.kind && (e.methodId ?? null) === (current.methodId ?? null))
        .sort((a, b) => a.position - b.position || a.createdAt.localeCompare(b.createdAt));
      const idx = siblings.findIndex((e) => e.id === entryId);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (idx < 0 || swapIdx < 0 || swapIdx >= siblings.length) return { success: true };

      // Renumber the two neighbours so positions are always distinct.
      const a = siblings[idx];
      const b = siblings[swapIdx];
      const aPos = swapIdx;
      const bPos = idx;
      const supabase = getTypedSupabaseClient();

      set((state) => ({
        entriesByDoc: {
          ...state.entriesByDoc,
          [current.v2momId]: (state.entriesByDoc[current.v2momId] ?? []).map((e) =>
            e.id === a.id ? { ...e, position: aPos } : e.id === b.id ? { ...e, position: bPos } : e,
          ),
        },
      }));

      const [ra, rb] = await Promise.all([
        supabase.from('v2mom_entries').update({ position: aPos }).eq('id', a.id),
        supabase.from('v2mom_entries').update({ position: bPos }).eq('id', b.id),
      ]);
      const error = ra.error ?? rb.error;
      if (error) {
        console.error('[V2momStore] moveEntry error:', error);
        void get().fetchV2moms();
        return { success: false, error: error.message };
      }
      return { success: true };
    },

    deleteEntry: async (entryId) => {
      const supabase = getTypedSupabaseClient();
      const current = findEntry(get(), entryId);
      if (!current) return { success: false, error: 'Entry not found' };

      set((state) => ({
        entriesByDoc: {
          ...state.entriesByDoc,
          [current.v2momId]: (state.entriesByDoc[current.v2momId] ?? []).filter(
            // A method takes its obstacles and measures with it (ON DELETE CASCADE).
            (e) => e.id !== entryId && e.methodId !== entryId,
          ),
        },
      }));

      const { error } = await supabase.from('v2mom_entries').delete().eq('id', entryId);
      if (error) {
        console.error('[V2momStore] deleteEntry error:', error);
        void get().fetchV2moms();
        return { success: false, error: error.message };
      }
      return { success: true };
    },
  };
});
